import { ConfigProvider, Layout, Select } from "antd";
import { CompanyForm } from "../components/form/CompanyForm";
import { ResidentForm } from "@/components/form/ResidentForm";
import "../styles/index.css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import enUS from "antd/locale/en_US";
import msMY from 'antd/locale/ms_MY';
import zhCN from "antd/locale/zh_CN";
import { Route, Routes, useSearchParams } from "react-router-dom";
import { Home } from "./Home";
import lingkarancekapLogoWhiteImg from "../assets/lingkarancekap_white_logo.png";
import i18n, { normalizeLanguage, type AppLanguage } from "../i18n";

const { Header, Footer, Content } = Layout;
const locales = { en: enUS, ms: msMY, "zh-CN": zhCN };
const languageOptions = [
  { value: "en", label: "English", shortLabel: "EN" },
  { value: "ms", label: "Bahasa Melayu", shortLabel: "BM" },
  { value: "zh-CN", label: "中文", shortLabel: "CN" },
];

export function App() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedLanguage = normalizeLanguage(searchParams.get("lang"));
  const [language, setLanguage] = useState<AppLanguage>(
    () => requestedLanguage ?? normalizeLanguage(localStorage.getItem("language")) ?? "en",
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (requestedLanguage && requestedLanguage !== language) {
      setLanguage(requestedLanguage);
    }
  }, [language, requestedLanguage]);

  useEffect(() => {
    localStorage.setItem("language", language);
    void i18n.changeLanguage(language);
  }, [language]);

  const changeLanguage = (nextLanguage: AppLanguage) => {
    setLanguage(nextLanguage);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("lang", nextLanguage);
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <ConfigProvider
      locale={locales[language]}
      theme={{
        token: {
          fontFamily: '"Nunito Sans", sans-serif',
          colorPrimary: '#1e72b8',
          colorText: '#ffffff',
          colorBgContainer: '#1e72b8',
          colorBorder: '#3a8fd1',
          colorTextPlaceholder: 'rgba(255, 255, 255, 0.45)',
        },
        components: {
          Layout: { bodyBg: 'transparent', headerBg: 'transparent' },
          Card: { colorBgContainer: 'rgba(30, 114, 184, 0.92)', colorText: '#ffffff', colorTextHeading: '#ffffff', colorBorderSecondary: '#3a8fd1' },
          Input: {
            colorBgContainer: '#175688',
            colorText: '#ffffff',
            colorTextPlaceholder: 'rgba(255,255,255,0.45)',
            colorBorder: '#ffffff',
            hoverBorderColor: '#ffffff',
            activeBorderColor: '#ffffff',
          },
         Select: {
            colorBgContainer: '#175688',
            colorText: '#ffffff',
            optionSelectedBg: '#1e72b8',
            colorBorder: '#ffffff',
            colorBgElevated: '#175688',
            controlItemBgHover: '#1e72b8',
            controlItemBgActive: '#1e5a8a',
          },
          Checkbox: {
            colorText: '#ffffff',
            colorBgContainer: '#ffffff',
            colorBorder: '#ffffff',
          },
          Form: { labelColor: '#ffffff' },
          Divider: { colorText: '#ffffff', colorSplit: 'rgba(255,255,255,0.3)' },
          Button: {
            colorText: '#1e72b8',
            colorBgContainer: '#ffffff',
            colorBorder: '#1e72b8',
            colorBgContainerDisabled: '#d9d9d9',
            colorTextDisabled: 'rgba(0,0,0,0.25)',
            colorBorderDisabled: '#d9d9d9',
            colorBgTextHover: '#f0f0f0',
            primaryColor: '#1e72b8',       // text color for primary button
            colorPrimary: '#ffffff',        // background for primary button
            colorPrimaryHover: '#f0f0f0',  // hover background for primary button
            colorPrimaryActive: '#e0e0e0', // active/click background
          },
          Upload: { colorText: '#ffffff' },
          Message: {
            contentBg: '#175688',
            colorText: '#ffffff',
            fontSize: 17,
          },
        }
      }}
    >
      <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }} className="page-layout">
        <Header style={{
          background: 'rgba(30, 114, 184, 0.9)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          <img src={lingkarancekapLogoWhiteImg} alt="Logo" style={{ height: '80px', marginRight: '12px' }} />
          <Select<AppLanguage>
            className="header-language-select"
            aria-label="Select language"
            value={language}
            options={languageOptions}
            onChange={changeLanguage}
            labelRender={({ value }) =>
              languageOptions.find(option => option.value === value)?.shortLabel ?? "EN"
            }
            popupMatchSelectWidth={160}
            style={{ width: 76, marginLeft: "auto" }}
          />
        </Header>

        <Content style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="app">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/resident" element={<ResidentForm />} />
            <Route path="/company" element={<CompanyForm />} />
          </Routes>
        </Content>

        <Footer style={{
          background: 'rgba(30, 114, 184, 0.9)',
          textAlign: 'center',
          color: '#ffffff',
          padding: '16px 24px',
        }}>
          © {new Date().getFullYear()} Lingkaran Cekap Sdn Bhd 202001028343 (1384663-M). {t("All Rights Reserved.")}
        </Footer>
      </Layout>
    </ConfigProvider>
  );
}

export default App;
